import React, { useEffect, useState } from 'react';
import { binaryToContext, canReducePerk, contextToBinary, createEmptyPerks, iconCircle, levelForRevelation, perkAvailable, sum } from './utils';
import data from '../data.yaml';

export interface Context {
  level: number;
  vocation: Vocation;
  perks: Record<number, number>;
  revelation: number[];
  pointsMax: number;
  pointsLeft: number;
  selectedPerk: number | null;
  prompt: string;

  setLevel: (level: number) => void;
  setVocation: (vocation: Vocation) => void;
  setCharacter: (level: number, vocation: Vocation) => void;
  setPerk: (id: number, value: number) => void;
  selectPerk: (id: number | null) => void;
  reset: () => void;
  setPrompt: (prompt : string) => void;
}

export const RootContext = React.createContext<Context>({
  level: -1,
  vocation: 'knight',
  perks: {},
  revelation: [],
  pointsMax: 0,
  pointsLeft: 0,
  selectedPerk: null,
  prompt: '',

  setLevel() {},
  setVocation() {},
  setCharacter() {},
  setPerk() {},
  selectPerk() {},
  reset() {},
  setPrompt() {},
});

interface Props {
  children: React.ReactNode;
  onChange?: () => void;
}

export const RootContextProvider: React.FC<Props> = ({ children, onChange }) => {
  let saved: any = {};
  try {
    saved = binaryToContext(location.hash.slice(1));
  } catch (e) {}

  const [ level, setLevel ] = useState<Context['level']>(saved.level ?? 0);
  const [ vocation, setVocation ] = useState<Context['vocation']>(saved.vocation ?? 'knight');
  const [ perks, setPerks ] = useState<Context['perks']>(saved.perks ?? createEmptyPerks());
  const [ selectedPerk, selectPerk ] = useState<Context['selectedPerk']>(null);
  const [ prompt, setPrompt ] = useState<Context['prompt']>('');

  const pointsMax = Math.max(0, level - 50);
  const pointsLeft = Math.max(0, pointsMax - sum(Object.values(perks)));
  const revelation = new Array(4).fill(0).map((_, i) => levelForRevelation(perks, i));

  useEffect(() => {
    location.hash = contextToBinary({ level, vocation, perks });
  }, [ level, vocation, perks]);

  function GeneratePromptCode() {
    let cmd = "!wheel ";
      const originalSlots = [
          2, 8, 9, 18, 19, 20, 28, 29, 34, -1,
          3, 10, 11, 21, 22, 23, 30, 31, 35, -1,
          1, 7, 6, 17, 16, 15, 27, 26, 33, -1,
          0, 5, 4, 14, 13, 12, 25, 24, 32, -1
      ];

      for(let i = 0; i < 40; i++) {
        if ((i+1) %10 == 0) {
          cmd += '0';
        } else{
          cmd += ''+(perks[originalSlots[i]] || 0);
        }
        if (i < 39) {
          cmd += ":";
        }
      }
      setPrompt(cmd);
  }

  useEffect(() => {
    GeneratePromptCode();
  }, [perks])

  return <RootContext.Provider value={{
    level,
    vocation,
    perks,
    revelation,
    pointsMax,
    pointsLeft,
    selectedPerk,
    prompt,
    setPrompt,

    setLevel,

    setVocation(vocation) {
      setVocation(vocation);
      setPerks(createEmptyPerks());
      onChange?.();
    },

    setCharacter(level, vocation) {
      setLevel(level);
      setVocation(vocation);
      setPerks(createEmptyPerks());
      onChange?.();
    },

    setPerk(id: number, value: number) {
      setPerks((currentPerks) => {
        const current = currentPerks[id];
        const circle = iconCircle(id);
        const maxPoints = data.pointsPerCircle[circle];

        if (value < current && !canReducePerk(perks, id) || !perkAvailable(currentPerks, id)) {
          return currentPerks;
        }

        const clampedValue = current + Math.min(value - current, pointsLeft);

        return {
          ...currentPerks,
          [id]: Math.max(0, Math.min(maxPoints, clampedValue)),
        }
      });

      onChange?.();
    },

    selectPerk,

    reset() {
      setPerks(createEmptyPerks());
	  setLevel(0);
      onChange?.();
    }
  }}>
    { children }
  </RootContext.Provider>
};
